import { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, Loader2, Plus, Minus, Send, X, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { mutate as offlineMutate } from "@/offline/apiClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import UomSelector, { defaultUomFor, allowedUomsFor } from "@/components/UomSelector";

// Phase 26D.2 cart entry shape:
//   { qty: <number, ENTERED>, uom: "PCS" | "KG" | "BAG" }
// `qty` is what the field user typed, `uom` is the unit of that qty. Canonical
// qty_pcs + qty_kg are computed server-side by the UOM engine — client only
// sends `entered_uom` + `entered_qty`. The offline outbox replays the same
// payload verbatim.
export default function OrderNew() {
  const nav = useNavigate();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const retailerId = params.get("retailer_id");
  const visitId = params.get("visit_id");
  const [retailer, setRetailer] = useState(null);
  const [lastOrder, setLastOrder] = useState(null);
  const [showLast, setShowLast] = useState(false);
  const [skus, setSkus] = useState([]);
  const [q, setQ] = useState("");
  const [cart, setCart] = useState({});   // sku_id → { qty, unit }
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [signatureRef, setSignatureRef] = useState(null);
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    if (!retailerId) { toast.error("Missing retailer_id"); nav(-1); return; }
    // Retailer must resolve first — the SKU catalog is scoped to *its*
    // distributor (only SKUs that distributor actually stocks), so the
    // /skus call needs distributor_id up front rather than fetching the
    // full company-wide catalog in parallel.
    api.get(`/retailers?page_size=1&q=${retailerId.slice(0, 8)}`)
      .then(r => r.data.items?.find(i => i.id === retailerId))
      .catch(() => null)
      .then((r) => {
        setRetailer(r);
        return Promise.all([
          api.get(`/retailers/${retailerId}/last-order`).catch(() => ({ data: { order: null } })),
          api.get("/skus", { params: { page_size: 100, is_active: true, is_orderable: true, distributor_id: r?.distributor_id } }),
        ]);
      })
      .then(([{ data: lo }, { data: sk }]) => {
        setLastOrder(lo.order); setSkus(sk.items || []);
      });
  }, [retailerId, nav]);

  const filtered = skus.filter(s => !q || s.name?.toLowerCase().includes(q.toLowerCase())
                                    || s.sku_code?.toLowerCase().includes(q.toLowerCase()));

  const skuMap = Object.fromEntries(skus.map(s => [s.id, s]));

  const addLine = (sku) => setCart(c => {
    const cur = c[sku.id] || { qty: 0, uom: defaultUomFor(sku, { excludePcs: true }) };
    return { ...c, [sku.id]: { ...cur, qty: (cur.qty || 0) + 1 } };
  });
  const decLine = (sku) => setCart(c => {
    const cur = c[sku.id] || { qty: 0, uom: defaultUomFor(sku, { excludePcs: true }) };
    const nx = (cur.qty || 0) - 1;
    if (nx <= 0) { const { [sku.id]: _, ...rest } = c; return rest; }
    return { ...c, [sku.id]: { ...cur, qty: nx } };
  });
  const setLineQty = (sku, qty) => setCart(c => {
    const cur = c[sku.id] || { qty: 0, uom: defaultUomFor(sku, { excludePcs: true }) };
    const v = Math.max(0, Number(qty) || 0);
    if (v === 0) { const { [sku.id]: _, ...rest } = c; return rest; }
    return { ...c, [sku.id]: { ...cur, qty: v } };
  });
  const setLineUom = (sku, uom) => setCart(c => {
    if (!c[sku.id]) return c;
    if (!allowedUomsFor(sku, { excludePcs: true }).includes(uom)) {
      toast.error(`${sku.sku_code}: ${uom} not enabled on this SKU`);
      return c;
    }
    return { ...c, [sku.id]: { ...c[sku.id], uom } };
  });

  // Debounced live quote — server converts entered_uom+entered_qty → canonical
  // qty_pcs and returns any fractional-pc warnings we can surface inline.
  useEffect(() => {
    const lines = Object.entries(cart).map(([sku_id, entry]) => ({
      sku_id, entered_uom: entry.uom, entered_qty: Number(entry.qty) || 0,
    })).filter(l => l.entered_qty > 0);
    if (lines.length === 0) { setQuote(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/orders/quote", { retailer_id: retailerId, lines });
        setQuote(data);
      } catch (e) { /* silent */ }
    }, 300);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, retailerId]);

  const captureSignature = async () => {
    setSigning(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = 400; canvas.height = 200;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, 400, 200);
      ctx.strokeStyle = "#000"; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(50, 100); ctx.quadraticCurveTo(200, 40, 350, 120); ctx.stroke();
      const blob = await new Promise((r) => canvas.toBlob(r, "image/png"));
      const form = new FormData();
      form.append("file", blob, "signature.png");
      form.append("context", "signature");
      const { data } = await api.post("/photos/upload", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      setSignatureRef(data.ref);
      toast.success("Signature captured");
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setSigning(false); }
  };

  const submit = async () => {
    if (Object.keys(cart).length === 0) return toast.error("Add at least one SKU");
    if (!signatureRef) return toast.error("Retailer signature required");
    setBusy(true);
    try {
      const gps = await new Promise((res) => {
        if (!navigator.geolocation) return res(null);
        navigator.geolocation.getCurrentPosition(
          (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
          () => res(null), { timeout: 4000 },
        );
      });
      const linesPayload = Object.entries(cart).map(([sku_id, entry]) => ({
        sku_id,
        entered_uom: entry.uom,
        entered_qty: Number(entry.qty) || 0,
      }));
      const body = {
        retailer_id: retailerId, visit_id: visitId || null,
        lines: linesPayload,
        signature_ref: signatureRef, gps, notes: notes || null,
        client_booking_ts: new Date().toISOString(),
        idempotency_key: (crypto.randomUUID?.() || `ord-${Date.now()}`),
      };
      const r = await offlineMutate({ method: "POST", path: "/orders", body,
                                      entity_type: "orders" });
      if (!r.immediate) toast.info("Queued offline");
      else toast.success(`Order ${r.response?.order_no || ""} booked`);
      nav("/orders", { replace: true });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 pb-40 space-y-3" data-testid="order-new-screen">
      <button onClick={() => nav(-1)} className="text-sm text-neutral-500">← Back</button>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-400">New order</div>
        <h1 className="text-lg font-extrabold text-neutral-900">{retailer?.name || "…"}</h1>
        <div className="text-xs text-neutral-500">{retailer?.code}</div>
      </div>

      {/* Last order strip */}
      {lastOrder && (
        <Card className="border-neutral-200 rounded-2xl">
          <CardContent className="p-3">
            <button onClick={() => setShowLast(v => !v)}
                    className="w-full flex items-center justify-between text-sm"
                    data-testid="last-order-toggle">
              <div className="text-neutral-600">Last order: <span className="font-mono text-xs">{lastOrder.order_no}</span></div>
              {showLast ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {showLast && (
              <div className="mt-2 space-y-1 text-xs" data-testid="last-order-detail">
                <div className="text-neutral-500">{lastOrder.order_date} · Net ₹{lastOrder.net_amount}</div>
                {(lastOrder.top_5_lines || []).map(l => (
                  <div key={l.id} className="flex justify-between">
                    <span>{l.sku_code_snapshot} × {l.qty}</span>
                    <span>₹{l.line_total}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* SKU search */}
      <Input placeholder="Search SKUs..." value={q} onChange={e => setQ(e.target.value)}
             data-testid="sku-search" />

      <div className="space-y-2" data-testid="sku-list">
        {filtered.map(s => {
          const entry = cart[s.id];
          const qty = entry?.qty || 0;
          const uom = entry?.uom || defaultUomFor(s, { excludePcs: true });
          const allowed = allowedUomsFor(s, { excludePcs: true });
          // Look up any live quote warning for this line
          const qLine = (quote?.lines || []).find(l => l.sku_id === s.id);
          const warnings = qLine?.uom_warnings || null;
          const qtyKgCanonical = qLine?.qty_kg;
          return (
            <Card key={s.id} className="border-neutral-200 rounded-xl">
              <CardContent className="p-3 space-y-1.5">
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-neutral-400">
                      {s.sku_code} · {allowed.join(" / ")}
                    </div>
                    <div className="text-sm font-semibold text-neutral-900 truncate">{s.name}</div>
                    <div className="text-[11px] text-neutral-500">₹{s.base_price} · GST {s.gst_pct}%</div>
                  </div>
                  {qty > 0 ? (
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="outline" onClick={() => decLine(s)}
                              className="h-8 w-8 rounded-full"
                              data-testid={`sku-dec-${s.sku_code}`}>
                        <Minus className="h-3.5 w-3.5" />
                      </Button>
                      <Input type="number" value={qty} onChange={e => setLineQty(s, e.target.value)}
                             className="w-16 h-8 text-center px-1"
                             data-testid={`sku-qty-${s.sku_code}`} />
                      <Button size="icon" onClick={() => addLine(s)}
                              className="h-8 w-8 rounded-full tulsi-gradient text-white"
                              data-testid={`sku-inc-${s.sku_code}`}>
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => addLine(s)}
                            className="h-8 rounded-lg" data-testid={`sku-add-${s.sku_code}`}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add
                    </Button>
                  )}
                </div>

                {qty > 0 && (
                  <div className="flex items-center justify-between pt-1"
                       data-testid={`sku-line-controls-${s.sku_code}`}>
                    {/* Order entry: Kg / Bag / Bunch only — PCS excluded */}
                    <UomSelector
                      sku={s}
                      value={uom}
                      onChange={(u) => setLineUom(s, u)}
                      testId={`sku-uom-${s.sku_code}`}
                      excludePcs
                    />
                    <div className="text-[10px] text-neutral-500"
                         data-testid={`sku-line-summary-${s.sku_code}`}>
                      {qtyKgCanonical != null
                        ? `${qtyKgCanonical} kg`
                        : `${qty} ${uom.toLowerCase()}`}
                    </div>
                  </div>
                )}

                {warnings && warnings.length > 0 && (
                  <div className="flex items-start gap-1.5 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1"
                       data-testid={`sku-line-warning-${s.sku_code}`}>
                    <AlertTriangle className="h-3 w-3 flex-shrink-0 mt-0.5" />
                    <span>{warnings[0]}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Notes */}
      <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Order notes (optional)" data-testid="order-notes" />

      {/* Signature */}
      {!signatureRef ? (
        <Button variant="outline" onClick={captureSignature} disabled={signing}
                className="w-full h-12 rounded-xl" data-testid="capture-signature">
          {signing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Capture retailer signature
        </Button>
      ) : (
        <div className="text-sm text-green-700 flex items-center gap-1 justify-center py-2"
             data-testid="signature-captured">
          ✓ Signature captured
        </div>
      )}

      {/* Sticky cart bottom sheet */}
      {quote && (
        <div className="fixed bottom-16 left-0 right-0 mx-3 rounded-2xl border border-neutral-200 bg-white shadow-xl p-3 space-y-1"
             data-testid="order-cart-panel">
          <div className="flex items-center justify-between text-xs text-neutral-500">
            <span>{Object.keys(cart).length} SKU{Object.keys(cart).length !== 1 ? "s" : ""}</span>
            <span>Gross ₹{quote.totals.gross} · Tax ₹{quote.totals.tax}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-xs text-neutral-500">Net total</div>
            <div className="text-2xl font-black text-[#E5197F]" data-testid="cart-net-total">₹{quote.totals.net}</div>
          </div>
          <Button onClick={submit} disabled={busy || !signatureRef}
                  className="w-full h-11 tulsi-gradient text-white rounded-xl font-bold"
                  data-testid="submit-order">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Submit order
          </Button>
        </div>
      )}
    </div>
  );
}
